/* eslint-disable no-console */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './use-auth';

export interface RequireAuthProps {
  children: React.ReactNode;
  fallback?: React.ComponentType;
  redirectPath?: string;
  onAuthRequired?: (currentPath: string) => void;
  enableDebugLogs?: boolean;
  requireMfa?: boolean;
  requiredScopes?: string[];
  maxSessionAge?: number; // in minutes
  /** Route path to the login page (default '/login'). */
  loginPath?: string;
}

/**
 * Enhanced RequireAuth component with better error handling and customization options
 * Redirect Strategy:
 * - When unauthenticated, we set originalUri (desired protected path) via useAuth.setOriginalUri
 * - Then we Navigate to the login route WITHOUT a redirect query param
 * - Login flow & Okta's callback restore originalUri automatically
 * This keeps redirect logic centralized in Okta, avoids duplicate restore layers,
 * and removes reliance on sessionStorage or query params.
 */
export const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  fallback: Fallback,
  redirectPath,
  enableDebugLogs = false,
  loginPath = '/login',
}) => {
  const auth = useAuth();
  const location = useLocation();

  // Pending state – show fallback or spinner
  if (auth.isPending) {
    if (Fallback) {
      return <Fallback />;
    }

    return (
      <div className='flex items-center justify-center min-h-screen bg-background'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2'></div>
          <div className='text-muted-foreground text-sm'>Validating session…</div>
        </div>
      </div>
    );
  }

  // Unauthenticated – persist intended path via Okta originalUri then go to login route
  if (!auth.isAuthenticated) {
    const target = redirectPath || location.pathname + location.search + location.hash;
    // Avoid overwriting an existing originalUri if one is already stored (e.g., set by login page ?from flow)
    try {
      const maybeExisting = (auth as unknown as { authState?: { originalUri?: string } }).authState
        ?.originalUri as string | undefined; // best-effort, SDK keeps this internal typically
      if (!maybeExisting) {
        auth.setOriginalUri(target);
      }
    } catch {
      auth.setOriginalUri(target);
    }
    if (enableDebugLogs) {
      console.log('RequireAuth: unauthenticated, set originalUri & navigating to login:', {
        originalUri: target,
      });
    }

    return <Navigate to={loginPath} replace />;
  }

  // Authenticated – render protected children
  return children as React.ReactElement;
};
